import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import * as echarts from "echarts/core";
import { ElementEvent } from "echarts/core";
import { DataZoomComponentOption, LineSeriesOption, TooltipOption, XAXisOption, YAXisOption } from "echarts/types/dist/shared";
import equal from "fast-deep-equal";
import { EMPTY, Observable, Subject, Subscription, catchError, combineLatest, fromEvent, map, startWith, take } from "rxjs";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IKpiSnapshot } from "@gtmhub/kpis";
import { createErrorObject } from "@webapp/error-handling/error-util";
import { KpiProjection } from "@webapp/kpis/models/kpi-projections.models";
import { Kpi, KpiTimeFilter } from "@webapp/kpis/models/kpis.models";
import { KpiProjectionsFacade } from "@webapp/kpis/services/kpi-projections/kpi-projections-facade.service";
import dayjs from "@webapp/shared/libs/dayjs";
import { ShortNumberPipe } from "@webapp/shared/pipes/short-number/short-number.pipe";
import { KpiDetailsStateProxyService } from "../../services/kpi-details-state-proxy.service";
import { KpiDetailsStateService } from "../../services/kpi-details-state.service";
import { ECOption, EGraphicComponentOption, IKpiChartDynamicContext, IKpiChartPoint, IKpiChartStaticContext, KpiChartCommentsState } from "../kpi-chart.models";
import {
  CHART_NUMBER_OF_ACCESSIBLE_YEAR_IN_FUTURE,
  CHART_POINT_DATE_FORMAT,
  chartTimeFiltersMaxAndMinDateRanges,
  formatKpiValue,
  getLeadingComments,
  isProjectionPoint,
  kpiChartCrosshairFormatter,
  kpiChartPointTooltipFormatter,
  kpiChartYAxisLabelFormatter,
  kpiChartYAxisMax,
  kpiChartYAxisMin,
  kpiDragAndDropPointTooltipFormatter,
} from "../utils";
import { KpiChartCommentsBuilder } from "./kpi-chart-comments.builder";

@Injectable({
  providedIn: "any",
})
export class KpiChartOptionsBuilder {
  /**
   * @param staticContext contains data that initially used to build the chart and is not mutated during drag and drop actions
   */
  private staticContext: IKpiChartStaticContext = { snapshotsDateRange: {} } as IKpiChartStaticContext;
  /**
   * @param dynamicContext on drag action the chart points and projection draggable points are being changed, hence they are mutable and stored separately from the data initially used to build the chart
   */
  private dynamicContext: IKpiChartDynamicContext = {} as IKpiChartDynamicContext;

  private dndGraphicPointClicked$: Subject<{ point: IKpiChartPoint; event: ElementEvent }> = new Subject<{ point: IKpiChartPoint; event: ElementEvent }>();

  public getDndGraphicPointClicked$(): Observable<{ point: IKpiChartPoint; event: ElementEvent }> {
    return this.dndGraphicPointClicked$.asObservable();
  }
  // this is needed because if we have click event, the dragend event is triggered by default (bacause of mouseup)
  // but we need to trigger dragend actions only when there was previously a drag action initated
  private isDragInProgress = false;

  private currentActiveSubscriptions: Subscription[] = [];

  constructor(
    private shortNumberPipe: ShortNumberPipe,
    private kpiProjectionFacade: KpiProjectionsFacade,
    private kpiDetailsService: KpiDetailsStateService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private kpiChartCommentsBuilder: KpiChartCommentsBuilder,
    private kpiDetailsStateProxyService: KpiDetailsStateProxyService
  ) {}

  public forKpi(kpi: Kpi): KpiChartOptionsBuilder {
    this.reset();
    this.staticContext.kpi = kpi;

    return this;
  }

  public inChartInstance(chart: echarts.ECharts): KpiChartOptionsBuilder {
    this.staticContext.chart = chart;
    return this;
  }

  public withSnapshots(snapshots: IKpiSnapshot[]): KpiChartOptionsBuilder {
    const snapshotChartPoints: IKpiChartPoint[] = snapshots.map(
      (s): IKpiChartPoint => ({
        date: s.key,
        snapshot: s.value,
        projection: null,
      })
    );

    this.staticContext.snapshotsDateRange = {
      max: snapshotChartPoints.length ? snapshotChartPoints[snapshotChartPoints.length - 1].date : undefined,
      min: snapshotChartPoints.length ? snapshotChartPoints[0].date : undefined,
    };
    this.dynamicContext.chartPoints = (this.dynamicContext.chartPoints || []).concat(snapshotChartPoints);

    return this;
  }

  public withProjections(projections: KpiProjection[]): KpiChartOptionsBuilder {
    this.staticContext.initialProjections = projections;
    this.dynamicContext.projections = structuredClone(projections); // the array is updated onDrag and we deep copy it so the initial values are not affected

    const projectionChartPoints: IKpiChartPoint[] = projections.map(
      (p): IKpiChartPoint => ({
        date: p.date,
        snapshot: null,
        projection: p.value,
        projectionId: p.id,
      })
    );

    this.staticContext.projectionsDateRange = {
      max: projectionChartPoints.length ? projectionChartPoints[projectionChartPoints.length - 1].date : undefined,
      min: projectionChartPoints.length ? projectionChartPoints[0].date : undefined,
    };
    this.dynamicContext.chartPoints = (this.dynamicContext.chartPoints || []).concat(projectionChartPoints);

    return this;
  }

  public withTimeFilter(timeFilter: KpiTimeFilter): KpiChartOptionsBuilder {
    // the timefilter is applied only if we have any data
    // it is based on snapshots latest/earliest dates if we have snapshots
    // it is based on projections latest/earliest dates otherwise
    this.staticContext.timeFilter = timeFilter;

    const isTimeFilterSnapshotBased = this.staticContext.snapshotsDateRange.max && this.staticContext.snapshotsDateRange.min;
    const maxSnapshotOrProjectionDate = isTimeFilterSnapshotBased ? this.staticContext.snapshotsDateRange.max : this.staticContext.projectionsDateRange.max;

    if (timeFilter && timeFilter !== "all-time") {
      this.setMinAndMaxPointOnInitForNotAllTimeFilter(timeFilter, maxSnapshotOrProjectionDate);
    } else if (timeFilter === "all-time") {
      const minSnapshotOrProjectionDate = isTimeFilterSnapshotBased ? this.staticContext.snapshotsDateRange.min : this.staticContext.projectionsDateRange.min;
      this.setMinAndMaxPointOnInitForAllTimeFilter(timeFilter, maxSnapshotOrProjectionDate, minSnapshotOrProjectionDate);
    }

    return this;
  }

  private setMinAndMaxPointOnInitForNotAllTimeFilter(timeFilter: KpiTimeFilter, maxSnapshotOrProjectionDate: string): void {
    const { min, max } = chartTimeFiltersMaxAndMinDateRanges.get(timeFilter);
    this.staticContext.minPointOnInit = dayjs(maxSnapshotOrProjectionDate).subtract(min, "day").format(CHART_POINT_DATE_FORMAT);
    this.staticContext.maxPointOnInit = dayjs(maxSnapshotOrProjectionDate).add(max, "day").format(CHART_POINT_DATE_FORMAT);
  }

  private setMinAndMaxPointOnInitForAllTimeFilter(timeFilter: KpiTimeFilter, maxSnapshotOrProjectionDate: string, minSnapshotOrProjectionDate: string): void {
    this.staticContext.minPointOnInit = dayjs(minSnapshotOrProjectionDate).format(CHART_POINT_DATE_FORMAT);
    const kpiLife = dayjs(maxSnapshotOrProjectionDate).diff(dayjs(minSnapshotOrProjectionDate));
    this.staticContext.maxPointOnInit = dayjs(maxSnapshotOrProjectionDate)
      .add(kpiLife / 2)
      .format(CHART_POINT_DATE_FORMAT);
  }

  public build(options: { canEditKpi: boolean }): void {
    if (!this.staticContext.chart) {
      throw new Error("Cannot build chart without chart instance");
    }

    this.unsubscribe();
    Object.freeze(this.staticContext);

    this.staticContext.chart.clear();

    this.buildAndSetInitialOption();
    if (options.canEditKpi) {
      this.buildAndSetDndOption();
      this.staticContext.chart.on("datazoom", this.buildAndSetDndOption);
    }

    this.subscribeToCommentsChange();
  }

  public unsubscribe(): void {
    this.currentActiveSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.currentActiveSubscriptions = [];
    this.removeChartListeners();
  }

  private removeChartListeners(): void {
    this.staticContext.chart?.off("datazoom", this.buildAndSetDndOption);
    this.staticContext.chart?.off("datazoom", this.buildAndSetComments);
  }

  private subscribeToCommentsChange(): void {
    const commentsSubscription = combineLatest({
      comments: this.kpiDetailsService.getComments$(),
      firstCommentContext: this.kpiDetailsService.getAddFirstCommentToPointContext$(),
      openComment: this.kpiDetailsStateProxyService.getOpenComment$(),
      resize: fromEvent(window, "resize").pipe(
        map((event) => event.target as Window),
        startWith(window)
      ),
    }).subscribe(({ comments, firstCommentContext, openComment }) => {
      // TODO: on comment created or on inital load of open comment the comments are built twice; refactor with: https://quantive-inc.atlassian.net/browse/GVS-40988
      const { projectionsLeadingCommentsMap, snapshotsLeadingCommentsMap } = getLeadingComments(comments);
      this.dynamicContext.projectionsLeadingCommentsMap = projectionsLeadingCommentsMap;
      this.dynamicContext.snapshotsLeadingCommentsMap = snapshotsLeadingCommentsMap;
      this.dynamicContext.firstCommentContext = firstCommentContext;
      this.dynamicContext.openComment = openComment;
      this.buildAndSetComments();
      this.staticContext.chart.off("datazoom", this.buildAndSetComments);
      this.staticContext.chart.on("datazoom", this.buildAndSetComments);
    });
    this.currentActiveSubscriptions.push(commentsSubscription);
  }

  private buildAndSetInitialOption(): void {
    const option: ECOption = this.buildChartSettings();
    this.staticContext.chart.setOption(option);
  }

  // arrow function is used to keep the method reference and 'this' context when used to add/remove listener
  private buildAndSetDndOption = (): void => {
    const projectionChartPoints = (this.dynamicContext.chartPoints || []).filter(isProjectionPoint);
    if (projectionChartPoints.length) {
      const dndOption: EGraphicComponentOption = {
        graphic: projectionChartPoints.map((item) => {
          return {
            type: "circle",
            position: this.staticContext.chart.convertToPixel({ seriesId: "projectionSeries" }, [item.date, item.projection]),
            shape: {
              cx: 0,
              cy: 0,
              r: 10,
            },
            invisible: true,
            draggable: true,
            ondrag: echarts.util.curry(
              this.onPointDrag,
              item.projectionId,
              this.staticContext,
              { projections: this.dynamicContext.projections, currentChartData: this.dynamicContext.chartPoints },
              {
                setIsDragInProgress: () => {
                  this.isDragInProgress = true;
                },
                updateCommentDropletOnProjectionDrag: (projectionId: string) => {
                  this.updateCommentDropletOnProjectionDrag(projectionId);
                },
              }
            ),
            ondragend: (event: echarts.ElementEvent): void => this.onPointDragEnd(event, item.projectionId),
            onclick: (event: echarts.ElementEvent): void => this.dndGraphicPointClicked$.next({ point: item, event: event }),
            onmouseover: (): void => {
              const projectionIndexInChartPointArray = this.dynamicContext.chartPoints.findIndex((point) => point.projectionId === item.projectionId);
              this.onMouseOver(projectionIndexInChartPointArray, this.staticContext.chart);
            },
            z: 100,
            tooltip: {
              // We have a tooltip for the graphic drag and drop points (as well as the chart elements - projections and snapshots) since the tooltip of the chart points (snapshots and projections built in buildAndSetInitialOption)
              // cannot be triggered by dispatchAction "showTip" since with the general options we have tooltip trigger "onAxis"
              // and showTip seems to fire only when trigger is "none" which would interfere with the crosshair behaviour.
              // We still need to trigger the chart point styles on hover and this is done onmouseover by dispatching "highlight" action
              // https://apache.github.io/echarts-handbook/en/concepts/event/
              // https://github.com/apache/echarts/issues/12844
              formatter: (): string =>
                kpiDragAndDropPointTooltipFormatter(
                  item,
                  { projections: this.dynamicContext.projections, kpi: this.staticContext.kpi },
                  this.dynamicContext.chartPoints,
                  this.shortNumberPipe
                ),
            },
          };
        }),
      };

      this.staticContext.chart.setOption(dndOption);
    }
  };

  private updateCommentDropletOnProjectionDrag(projectionId: string): void {
    if (this.dynamicContext.projectionsLeadingCommentsMap && !equal(this.dynamicContext.projectionsLeadingCommentsMap, {})) {
      const oldProjectionSeries = (this.staticContext.chart.getOption().series as LineSeriesOption[]).find((x) => x.id === "projectionSeries");
      const projectionSeriesUpdated = this.kpiChartCommentsBuilder.getUpdateProjectionSeriesOnProjectionDrag(this.staticContext, projectionId);
      if (projectionSeriesUpdated && !equal(oldProjectionSeries, projectionSeriesUpdated)) {
        this.staticContext.chart.setOption({ series: projectionSeriesUpdated });
      }
    }
  }

  // arrow function is used to keep the method reference and 'this' context when used to add/remove listener
  private buildAndSetComments = (): void => {
    let state: KpiChartCommentsState;
    if (this.dynamicContext.firstCommentContext?.inProgress) {
      state = "adding_first_comment";
    } else if (this.dynamicContext.openComment) {
      state = "any_comment_is_open";
    } else {
      state = "default";
    }

    const lineSeriesWithCommentPins = this.kpiChartCommentsBuilder.build(state, this.staticContext, this.dynamicContext);
    this.staticContext.chart.setOption({ series: lineSeriesWithCommentPins });
  };

  private onMouseOver(dataIndex: number, chart: echarts.ECharts): void {
    // we need this in order to display hover styles of projection point
    chart.dispatchAction({
      type: "highlight",
      seriesIndex: 1,
      dataIndex: dataIndex,
    });
  }

  private onPointDrag(
    this: { x: number; y: number },
    projectionId: string,
    staticContext: IKpiChartStaticContext,
    dynamicData: { projections: KpiProjection[]; currentChartData: IKpiChartPoint[] },
    functions: {
      setIsDragInProgress(): void;
      updateCommentDropletOnProjectionDrag(projectionId: string): void;
    }
  ): void {
    functions.setIsDragInProgress();
    functions.updateCommentDropletOnProjectionDrag(projectionId);
    const { chart, kpi } = staticContext;
    const [date, value] = chart.convertFromPixel({ seriesId: "projectionSeries" }, [this.x, this.y]);
    const formattedValue = formatKpiValue(value, kpi.formatting.fractionSize);
    const projectionIndexInCurrentChartData = dynamicData.currentChartData.findIndex((chartPoint) => chartPoint.projectionId === projectionId);
    dynamicData.currentChartData[projectionIndexInCurrentChartData] = {
      date: dayjs(date).format(CHART_POINT_DATE_FORMAT),
      snapshot: null,
      projection: formattedValue,
      projectionId: projectionId,
    };

    const projectionIndexInProjections = dynamicData.projections.findIndex((p) => p.id === projectionId);
    dynamicData.projections[projectionIndexInProjections] = {
      id: projectionId,
      value: formattedValue,
      date: dayjs(date).format(CHART_POINT_DATE_FORMAT),
    };

    chart.setOption({
      dataset: {
        source: dynamicData.currentChartData,
      },
    });
  }

  private onPointDragEnd(event: echarts.ElementEvent, projectionId: string): void {
    if (this.isDragInProgress) {
      const isPointDraggedInsideGrid = this.staticContext.chart.containPixel("grid", [event.target.x, event.target.y]);
      if (isPointDraggedInsideGrid) {
        this.onPointDragEndInsideGrid(projectionId);
      } else {
        this.kpiDetailsService.refreshChartData(); // if the point is dragged outside grid area, we are not patching the projection but displaying the previous values
      }
    }

    this.isDragInProgress = false;
  }

  private onPointDragEndInsideGrid(projectionId: string): void {
    const projection = this.dynamicContext.chartPoints.find((point) => point.projectionId === projectionId);

    const projectionPatch = { date: projection.date, value: projection.projection };
    const isPointDraggedToDateWithAnotherProjection = this.dynamicContext.projections.find(
      (projection) => projection.date === projectionPatch.date && projection.id !== projectionId
    );

    if (isPointDraggedToDateWithAnotherProjection) {
      this.kpiDetailsService.refreshChartData(); // if the point is dragged on a date we already have a projection, we are not patching the projection but displaying the previous values which are being held in the subjects in kpi-details-state.service.ts
    } else {
      this.onValidPointDragEnd(projectionPatch, projectionId);
    }
  }

  private onValidPointDragEnd(projectionPatch: Pick<KpiProjection, "date" | "value">, projectionIdToPatch: string): void {
    const shouldBuildChart = this.dynamicContext.projections.length > 2 && this.isChronologicalOrderOfProjectionsChanged();
    this.patchProjection(projectionPatch, projectionIdToPatch, this.staticContext.kpi.id, { shouldBuildChart: shouldBuildChart });
  }

  private isChronologicalOrderOfProjectionsChanged(): boolean {
    const initialProjectionsIds = this.staticContext.initialProjections.map((p) => p.id); // they come sorted by date from the backend
    const currProjectionIds = [...this.dynamicContext.projections].sort((a, b) => dayjs(a.date).diff(dayjs(b.date))).map((p) => p.id);

    return !equal(initialProjectionsIds, currProjectionIds);
  }

  // TODO: (Yana) this is the same as in projection form - discuss if and how to refactor
  private patchProjection(
    payload: Pick<KpiProjection, "date" | "value">,
    projectionId: string,
    kpiId: string,
    { shouldBuildChart }: { shouldBuildChart: boolean }
  ): void {
    this.kpiProjectionFacade
      .updateProjection$(kpiId, projectionId, payload, "dragging")
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          this.uiErrorHandlingService.handleModal(createErrorObject(error));
          return EMPTY;
        })
      )
      .subscribe(() => {
        this.kpiDetailsService.setShouldBuildChart(shouldBuildChart);
        this.kpiDetailsService.fetchKpiProjectionsAndComments(kpiId);
        this.buildAndSetDndOption(); // this is needed so the tooltip of the draggable points displays proper data after drag and drop
        this.kpiDetailsStateProxyService.onProjectionDragged({ id: projectionId, ...payload });
      });
  }

  private hasChartData(): boolean {
    return this.dynamicContext?.chartPoints?.length > 0;
  }

  private buildChartSettings(): ECOption {
    return {
      dataset: {
        source: this.hasChartData() ? this.dynamicContext.chartPoints : [],
      },
      series: this.buildSeriesSettings(),
      xAxis: this.buildXAxisSettings(),
      yAxis: this.buildYAxisSettings(),
      grid: this.buildGridSettings(),
      tooltip: this.buildTooltipSettings(),
      dataZoom: this.buildDataZoomSettings(),
      backgroundColor: "white",
      animation: true,
    };
  }

  private buildSeriesSettings(): LineSeriesOption {
    return [
      {
        id: "snapshotSeries",
        dimensions: ["date", "snapshot"],
        symbolSize: 0.1,
        type: "line",
        color: "#7E5FBF", // Purple 80
        emphasis: {
          scale: 100, // size scale multiplicator on hover
        },
      },
      {
        id: "projectionSeries",
        dimensions: ["date", "projection"],
        symbolSize: 10,
        symbol: "circle",
        type: "line",
        itemStyle: {
          color: "#417AEA", // gtmds_color_blue-6
          borderColor: "rgba(0, 0, 0, 0)",
          borderWidth: 10,
        },
        emphasis: {
          scale: 1.4, // size scale multiplicator on hover
          itemStyle: {
            color: {
              type: "radial",
              x: 0.5,
              y: 0.5,
              r: 0.5,
              colorStops: [
                {
                  offset: 0,
                  color: "#0057D7", // gtmds_color_blue-8 === @primary-color-8
                },
                {
                  offset: 0.7,
                  color: "#0057D7", // gtmds_color_blue-8 === @primary-color-8
                },
                {
                  offset: 0.7,
                  color: "rgba(0, 0, 0, 0)",
                },
                {
                  offset: 0.9,
                  color: "rgba(0, 0, 0, 0)",
                },
                {
                  offset: 0.9,
                  color: "#0057D7", // gtmds_color_blue-8 === @primary-color-8
                },
                {
                  offset: 1,
                  color: "#0057D7", // gtmds_color_blue-8 === @primary-color-8
                },
              ],
            },
          },
        },
        lineStyle: {
          color: "#417AEA", // gtmds_color_blue-6
          type: "dashed",
        },
      },
    ] as LineSeriesOption;
  }

  private buildXAxisSettings(): XAXisOption {
    return {
      type: "time",
      min: this.hasChartData() ? undefined : dayjs().startOf("year").format(CHART_POINT_DATE_FORMAT),
      max: this.hasChartData() ? this.getMaxAccessiblePoint() : dayjs().endOf("year").format(CHART_POINT_DATE_FORMAT),
      splitLine: {
        show: true,
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        hideOverlap: true,
      },
    };
  }

  private buildYAxisSettings(): YAXisOption {
    return {
      show: true,
      min: this.hasChartData() ? kpiChartYAxisMin : 0,
      max: this.hasChartData() ? kpiChartYAxisMax : 5000,
      splitLine: {
        show: true,
      },
      axisLabel: {
        formatter: (value: number): string => kpiChartYAxisLabelFormatter(value, this.shortNumberPipe),
      },
    };
  }

  private buildGridSettings(): unknown {
    return {
      show: true,
      backgroundColor: "white",
      top: 12,
      bottom: 32,
      left: 48, // the width of one time filter
      right: 0,
    };
  }

  private buildTooltipSettings(): TooltipOption {
    return this.hasChartData() ? this.buildTooltipSettingsForChartWithData() : undefined;
  }

  private buildTooltipSettingsForChartWithData(): TooltipOption {
    return {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        label: {
          show: true,
          // params are of type LabelFormatterParams but it is not exported from echarts/types
          formatter: (params): string => {
            return kpiChartCrosshairFormatter(params, this.shortNumberPipe);
          },
          backgroundColor: "#617294",
        },
      },
      renderMode: "html",
      padding: 12,
      borderColor: "#7485A2", // @global_color_gray_7
      formatter: (params): string =>
        kpiChartPointTooltipFormatter(params, { projections: this.dynamicContext.projections, kpi: this.staticContext.kpi }, this.shortNumberPipe),
    };
  }

  private buildDataZoomSettings(): DataZoomComponentOption[] {
    return this.hasChartData() ? this.buildDataZoomSettingsForChartWithData() : undefined;
  }

  private buildDataZoomSettingsForChartWithData(): DataZoomComponentOption[] {
    return [
      {
        id: "insideX",
        type: "inside",
        xAxisIndex: 0,
        filterMode: "none",
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        startValue: this.staticContext.minPointOnInit,
        endValue: this.staticContext.maxPointOnInit,
      },
      {
        id: "insideY",
        type: "inside",
        yAxisIndex: [0, 1],
        filterMode: "none",
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
      },
    ];
  }

  private reset(): void {
    this.dynamicContext = {} as IKpiChartDynamicContext;
    this.staticContext = {} as IKpiChartStaticContext;
  }

  private getMaxAccessiblePoint(): string {
    return dayjs().add(CHART_NUMBER_OF_ACCESSIBLE_YEAR_IN_FUTURE, "year").format(CHART_POINT_DATE_FORMAT);
  }
}
